import type { CartItemType } from '~/utils/api/cart';
import type { ProductType } from '~/utils/api/common';
import type { ProductVariantType } from '~/utils/api/product';
import { SCREEN_SIZES } from '~/utils/screen';
import { applyTwoPlusOnePromo, mergeSameSkuItems } from '~/utils/twoPlusOne';

const MODAL_KEY = 'product-cart';
const CART_DATA_VERSION = 1;
const CART_DATA_STORAGE_KEY = 'cart-data';

export const useProductCartStore = defineStore('cart', () => {
  const t = useNuxtApp().$i18n.t;
  const userStore = useUserStore();
  const commonStore = useCommonStore();
  const modalStore = useModalStore();
  const dataLayer = useDataLayer();
  const subscribeProductsStore = useSubscribeProductsStore();

  const opened = computed({
    get: () => !!modalStore.modalKeys[MODAL_KEY],
    set: (v) => modalStore.toggleModal(MODAL_KEY, v),
  });
  const open = () => modalStore.openModal(MODAL_KEY);
  const close = () => modalStore.closeModal(MODAL_KEY);

  const loading = ref(false);
  const cartData = ref<CartItemType[]>([]);
  const timers = ref<Record<number, NodeJS.Timeout>>({});
  let mutationRevision = 0;

  const offlineStore = useOfflineStore(cartData, CART_DATA_STORAGE_KEY, CART_DATA_VERSION);

  const cartKey = computed(() =>
    cartData.value
      .map((el) => el.skuId)
      .sort()
      .join(','),
  );
  const totalCount = computed(() => cartData.value.reduce((acc, item) => acc + (!item.isGift ? item.quantity : 0), 0));
  const totalCountAvailable = computed(() =>
    cartData.value.reduce((acc, item) => acc + (item.isAvailable && !item.isGift ? item.quantity : 0), 0),
  );

  const totalPriceAvailable = computed(() =>
    cartData.value.reduce((acc, item) => acc + (item.isAvailable && !item.isGift ? item.price * item.quantity : 0), 0),
  );

  const recommendations = useObservedData(cartKey, CartAPI.recommendations);

  const clearCartData = () => {
    mutationRevision += 1;
    cartData.value = [];
    offlineStore.reset();
  };

  const initCartData = () => {
    if (userStore.authorized) {
      CartAPI.contents().then((data) => {
        cartData.value = data.items;

        // Бэкенд вычистил из корзины закончившиеся товары — предупреждаем пользователя.
        if (data.itemsRemoved) {
          modalStore.showInfo({
            title: t('store.cart.message.stock_removed'),
            action: t('store.cart.message.continue'),
          });
        }
      });
    }

    offlineStore.init();
  };

  const checkDelta = (data: CartItemType[], skuId: number, quantity: number) => {
    // Сплит 2+1: товар в ответе корзины лежит несколькими строками (winner за 1 ₽ —
    // отдельной строкой), поэтому «итог по товару» — сумма по sku, а не первая
    // попавшаяся строка: первая строка никогда не держит всю сумму, и сравнение
    // с ней давало ложное «нет на складе» при каждом увеличении количества участника.
    const skuQty = data.filter((el) => el.skuId === skuId).reduce((acc, el) => acc + el.quantity, 0);

    if (quantity > skuQty) {
      modalStore.showInfo({
        title: t('store.cart.message.count_error'),
        action: t('store.cart.message.action'),
      });
    }
  };

  const saveItem = (skuId: number, quantity: number) => {
    CartAPI.updateItem({ body: { id: skuId, quantity } })
      .then((data) => {
        cartData.value = data.items;

        checkDelta(data.items, skuId, quantity);
      })
      .catch(() => {
        modalStore.showError({
          title: t('store.cart.message.update_error'),
          action: t('store.cart.message.action'),
        });
      });
  };

  const deleteItem = (skuId: number) => {
    CartAPI.removeItem({ body: { id: skuId } })
      .then((data) => {
        cartData.value = data.items;
      })
      .catch(() => {
        modalStore.showError({
          title: t('store.cart.message.update_error'),
          action: t('store.cart.message.action'),
        });
      });
  };

  // Гость: снимок корзины для synchronize берём в момент правки, а не в момент
  // срабатывания debounce. Ответ медленного synchronize может вернуться внутри
  // окна debounce (500мс) и откатить cartData — тогда таймер отправил бы уже
  // откаченное состояние, и правка пользователя терялась («нельзя менять
  // количество, возвращается»).
  let pendingSyncPayload: Array<{ id: number; quantity: number }> = [];
  const synchronize = (
    skuId: number,
    quantity: number,
    payload: Array<{ id: number; quantity: number }>,
    epoch: number,
  ) => {
    CartAPI.synchronize({ body: payload }).then((data) => {
      if (epoch !== mutationRevision) return;

      // Бэкенд применяет акцию «2+1» в synchronize (BasketUnauthorizedService →
      // TwoPlusOnePromo) — ответ приходит со сплитом, и applyTwoPlusOnePromo
      // пропускает его по маркеру twoPlusOneWinner. Эмуляция остаётся фолбэком
      // для старого бэкенда без сплита в synchronize.
      cartData.value = applyTwoPlusOnePromo(data.items, commonStore.products);

      if (quantity > 0) checkDelta(data.items, skuId, quantity);
    });
  };

  const updateCartItem = (item: CartItemType, productItem?: ProductType, append = false) => {
    // Split 2+1: один товар может лежать в корзине несколькими строками (winner за 1 ₽ —
    // отдельной строкой), поэтому «текущее количество» — сумма по sku, а не первая
    // попавшаяся строка. item.quantity при append=false — итог по товару: дельта,
    // посчитанная по первой строке, на split-корзине гасилась в ноль и PUT не уходил.
    const sameSkuItems = cartData.value.filter((el) => el.skuId === item.skuId);
    const currentQty = sameSkuItems.reduce((acc, el) => acc + el.quantity, 0);
    const quantityDelta = append ? item.quantity : item.quantity - currentQty;
    // Удаление строки, которая уже лежит с нулевым количеством, даёт нулевую дельту.
    // Такой товар видно в корзине, и выйти здесь значит не отправить запрос вообще —
    // для пользователя это «товар не удаляется».
    const isRemoval = !append && item.quantity <= 0 && sameSkuItems.length > 0;
    if (!quantityDelta && !isRemoval) return;
    mutationRevision += 1;

    dataLayer.append(
      {
        event: quantityDelta > 0 ? 'addToCart' : 'removeFromCart',
        ecommerce: {
          currencyCode: 'RUB',
          [quantityDelta > 0 ? 'add' : 'remove']: {
            products: [
              {
                id: item.skuId,
                name: item.name,
                price: item.price,
                quantity: Math.abs(quantityDelta) || currentQty || item.quantity,
                xml_id: productItem?.xmlId,
              },
            ],
          },
        },
      },
      ['user'],
    );

    let quantity = item.quantity;
    const first = sameSkuItems[0];
    if (first) {
      // Итог встаёт в первую строку, остальные строки sku склеиваются —
      // PUT возвращает корзину одной строкой на товар.
      quantity = (append ? currentQty : 0) + item.quantity;
      first.quantity = quantity;
      sameSkuItems.slice(1).forEach((el) => cartData.value.splice(cartData.value.indexOf(el), 1));
      if (quantity < 1) {
        cartData.value.splice(cartData.value.indexOf(first), 1);

        if (productItem?.refillId && subscribeProductsStore.productIds.some((id) => id === productItem.refillId))
          subscribeProductsStore.onDelete(productItem.refillId);
      }
    } else if (item.quantity > 0) {
      cartData.value.push(item);
    }

    if (timers.value[item.skuId]) clearTimeout(timers.value[item.skuId]);
    if (!userStore.authorized) {
      pendingSyncPayload = mergeSameSkuItems(cartData.value).map((el) => ({
        id: el.skuId,
        quantity: el.quantity,
      }));
    }
    timers.value[item.skuId] = setTimeout(() => {
      if (userStore.authorized) {
        loading.value = true;

        if (quantity > 0) saveItem(item.skuId, quantity);
        else deleteItem(item.skuId);
      } else synchronize(item.skuId, quantity, pendingSyncPayload, mutationRevision);

      delete timers.value[item.skuId];
    }, 500);
  };

  const updateCartItemByProductInfo = (productInfo: ProductType, quantity: number, append = false) => {
    if (productInfo.addToBasketId && productInfo.isAvailable) {
      updateCartItem(
        {
          quantity,
          skuId: productInfo.addToBasketId,
          productId: productInfo.id,
          name: productInfo.name,
          code: productInfo.code,
          price: productInfo.priceInfo.price,
          basePrice: productInfo.priceInfo.basePrice,
          isAvailable: productInfo.isAvailable,
        },
        productInfo,
        append,
      );
    }
  };

  const updateCartItemBySkuInfo = (
    skuInfo: ProductVariantType,
    productItem: ProductType,
    quantity: number,
    append = false,
  ) => {
    if (skuInfo.isAvailable) {
      updateCartItem(
        {
          quantity,
          skuId: skuInfo.id,
          productId: productItem.id,
          name: `${skuInfo.distinct}`,
          code: skuInfo.skuCode,
          price: skuInfo.price,
          basePrice: skuInfo.basePrice,
          isAvailable: skuInfo.isAvailable,
          isGift: skuInfo.isGift,
        },
        productItem,
        append,
      );
    }
  };

  const transferCartData = async () => {
    if (!userStore.authorized || !cartData.value.length) return;

    commonStore.pendingGuestTransfers += 1;
    return CartAPI.transfer({
      // cartData может быть в сплит-представлении (клиентский расчёт 2+1 для
      // анонима) — бэкенду нужна одна запись на товар с суммарным количеством.
      body: mergeSameSkuItems(cartData.value).map((el) => ({
        id: el.skuId,
        quantity: el.quantity,
      })),
    })
      .then((data) => {
        if (
          data.items.reduce((acc, el) => acc + el.quantity, 0) >
          cartData.value.reduce((acc, el) => acc + el.quantity, 0)
        ) {
          modalStore.showInfo({
            title: t('store.cart.message.transfer_info'),
            action: t('store.cart.message.continue'),
          });
        }

        cartData.value = data.items;
        offlineStore.reset();
      })
      .catch(() => {
        modalStore.showError({
          title: t('store.cart.message.transfer_error'),
          action: t('store.cart.message.action'),
        });
        commonStore.guestTransferFailed = true;
      })
      .finally(() => {
        commonStore.pendingGuestTransfers -= 1;
      });
  };

  watch(cartData, () => {
    loading.value = false;
  });

  return {
    opened,
    open,
    close,

    initialized: offlineStore.initialized,
    loading,
    cartData,
    getMutationRevision: () => mutationRevision,
    recommendations,
    totalCount,
    totalCountAvailable,
    totalPriceAvailable,
    clearCartData,
    initCartData,
    updateCartItem,
    updateCartItemByProductInfo,
    updateCartItemBySkuInfo,
    transferCartData,
  };
});
